import React, { useState } from 'react'; // NEW: Import useState
import { Vendor } from '../types/costwand.d';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table.tsx';
import { Button } from './ui/button.tsx';
import Pagination from './ui/Pagination.tsx';
import { Spinner } from './ui/spinner.tsx';
import { apiClient } from '../api/apiClient.tsx'; // NEW: Import apiClient
import { useWhitelabelContext } from '../context/WhitelabelContext.tsx'; // NEW: Import useWhitelabelContext

interface VendorTableProps {
  vendors: Vendor[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onEdit: (vendor: Vendor) => void;
  onDelete: (id: number) => void;
  loading: boolean;
  error: string | null;
  baseEndpoint: string; // NEW: Add baseEndpoint prop
}

const VendorTable: React.FC<VendorTableProps> = ({
  vendors,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  onEdit,
  onDelete,
  loading,
  error,
  baseEndpoint, // NEW: Destructure baseEndpoint
  entityId
}) => {
  const [exportLoading, setExportLoading] = useState(false); // NEW: State for export loading

  const { whitelabelConfig } = useWhitelabelContext();

  const getFieldConfig = (fieldId: string) =>
    whitelabelConfig.entities?.[entityId]?.fields?.[fieldId];

  const isFieldVisible = (fieldId: string) =>
    getFieldConfig(fieldId)?.visible !== false; // Default to visible

  const getFieldTitle = (fieldId: string, defaultTitle: string) =>
    getFieldConfig(fieldId)?.title || defaultTitle;

  const getEntityTitle = (defaultTitle: string) => 
    whitelabelConfig.entities?.[entityId].title || defaultTitle;

  const handleExportCsv = async () => {
    setExportLoading(true);
    try {
      await apiClient<void>(baseEndpoint, { downloadCsv: true });
    } catch (err: any) {
      // Error already handled by apiClient and globalErrorHandler
    } finally {
      setExportLoading(false);
    }
  };

  if (loading) return <p className="text-center text-muted-foreground flex items-center justify-center gap-2"><Spinner size="sm" /> Loading vendors...</p>;
  if (vendors.length === 0 && !loading) return <p className="text-center text-muted-foreground">No vendors found.</p>;

  return (
    <div className="rounded-md border">
      <div className="flex justify-end mb-4"> {/* NEW: Wrapper for export button */}
        <Button onClick={handleExportCsv} disabled={loading || exportLoading} variant="outline">
          {exportLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Exporting...</span> : 'Export to CSV'}
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Code</TableHead>
            {isFieldVisible('address') && <TableHead>Address</TableHead>}
            {isFieldVisible('city') && <TableHead>City</TableHead>}
            {isFieldVisible('phone') && <TableHead>Phone</TableHead>}
            {isFieldVisible('email') && <TableHead>Email</TableHead>}
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {vendors.map((vendor) => (
            <TableRow key={vendor.id}>
              <TableCell className="font-medium">{vendor.name}</TableCell>
              <TableCell>{vendor.code}</TableCell>
              {isFieldVisible('address') && <TableCell>{vendor.address}</TableCell>}
              {isFieldVisible('city') && <TableCell>{vendor.city}</TableCell>}
              {isFieldVisible('phone') && <TableCell>{vendor.phone}</TableCell>}
              {isFieldVisible('email') && <TableCell>{vendor.email}</TableCell>}
              <TableCell className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => onEdit(vendor)}>Edit</Button>
                <Button variant="destructive" size="sm" onClick={() => onDelete(vendor.id!)}>Delete</Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={onPageChange}
      />
    </div>
  );
};

export default VendorTable;